import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Card, Link, Stack } from '@lanekassen/ui';
import { HtmlString } from '@modulis/utils';
import { XMarkIcon } from '@navikt/aksel-icons';
import { InitPopup } from '../dto/InitPopup';

// https://hidde.blog/using-javascript-to-trap-focus-in-an-element/
function trapFocusWithinElement(element: HTMLElement | null): () => void {
  if (!element) return () => {};
  const keydown = (e: KeyboardEvent) => {
    const trappedElements = Array.from(
      element.querySelectorAll('a[href]:not([disabled]), button:not([disabled])'),
    ).filter((el) => {
      // Filtrer bort elementer som ikke kan få fokus på pga de er skjult
      const style = getComputedStyle(el);
      return !(style.display === 'none' || style.visibility === 'hidden');
    });
    const firstFocusableEl = trappedElements[0] as HTMLAnchorElement | HTMLButtonElement;
    const lastFocusableEl = trappedElements[trappedElements.length - 1] as HTMLAnchorElement | HTMLButtonElement;
    if (e.key !== 'Tab') {
      return;
    }

    // Hvis det er kunn en element, tillatt at brukeren kommer ut
    if (trappedElements.length === 1) return;

    if (e.shiftKey) {
      /* shift + tab */ if (document.activeElement === firstFocusableEl) {
        lastFocusableEl.focus();
        e.preventDefault();
      }
    } /* tab */ else if (document.activeElement === lastFocusableEl) {
      firstFocusableEl.focus();
      e.preventDefault();
    }
  };
  element.addEventListener('keydown', keydown);
  return () => element.removeEventListener('keydown', keydown);
}

export function Popup({ innhold, monitorEndepunkt }: InitPopup) {
  const [isVisible, setIsVisible] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);

  const mutateAsync = async (timer: number) => {
    const input = { popupNavn: innhold.popupNavn, hoursToAdd: timer };
    const res = await fetch(`${monitorEndepunkt}/OppdaterPopupCookie`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error(await res.text());
  };

  const skjulPopup = async () => {
    mutateAsync(innhold.timerTilPopupSkalVisesIgjen);
    setIsVisible(false);
  };

  const lenkeKlikket = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const timer = innhold.aldriVisIgjenNarLenkeTrykket ? 24 * 90 : innhold.timerTilPopupSkalVisesIgjen;
    mutateAsync(timer).then(() => {
      if (innhold?.lenke?.href) {
        if (innhold?.lenke?.target === '_blank') {
          window.open(innhold.lenke.href, '_blank');
        } else {
          window.location.href = innhold.lenke.href;
        }
      }
    });
  };

  useEffect(() => {
    if (headerRef.current && isVisible) {
      headerRef.current?.focus();
    }
  }, [headerRef.current, isVisible]);

  useEffect(() => {
    return trapFocusWithinElement(ref.current);
  }, [ref.current]);

  return (
    <Card
      sx={(theme) => ({
        display: isVisible ? 'block' : 'none',
        position: 'fixed',
        height: 'fit-content',
        inset: theme.spacing(4),
        marginTop: 'auto',
        marginRight: 0,
        marginLeft: 'auto',
        maxWidth: theme.breakpoints.values.sm,
        zIndex: 1000,
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          color: theme.palette.text.primary,
        },
      })}
      component="form"
      id={innhold?.popupNavn}
      ref={ref}
    >
      <Card.ActionArea ref={headerRef} onClick={skjulPopup}>
        <Card.Header level={1} size="lg" actions={<XMarkIcon aria-hidden /* TODO aria-label */ onClick={skjulPopup} />}>
          {innhold?.overskrift}
        </Card.Header>
      </Card.ActionArea>
      <Card.Content
        sx={{
          '@media (min-height: 1000px)': {
            maxHeight: '100%',
          },
          maxHeight: '50vh',
          overflowY: 'auto',
        }}
      >
        <HtmlString value={innhold?.tekst} />
      </Card.Content>
      <Card.Footer>
        <Stack spacing={{ xs: 2.5, md: 2 }} direction={{ md: 'row', sm: 'column' }} width="100%">
          <Link
            href={innhold?.lenke?.href}
            target={innhold?.lenke?.target ?? undefined}
            isExternal={innhold?.lenke?.target === '_blank'}
            onClick={(e) => lenkeKlikket(e)}
          >
            {innhold?.lenke?.text}
          </Link>
        </Stack>
      </Card.Footer>
    </Card>
  );
}
